/**
 * Friends store using Zustand.
 * Handles friend list, friend requests, and user search.
 */
import { create } from 'zustand';
import type {
  Friendship,
  FriendRequest,
  UserSearchResult,
  FriendStats,
} from '../types';
import { api } from '../api/client';

interface FriendsState {
  // State
  friends: Friendship[];
  incomingRequests: FriendRequest[];
  outgoingRequests: FriendRequest[];
  searchResults: UserSearchResult[];
  stats: FriendStats | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  fetchFriends: () => Promise<void>;
  fetchIncomingRequests: () => Promise<void>;
  fetchOutgoingRequests: () => Promise<void>;
  fetchStats: () => Promise<void>;
  sendFriendRequest: (receiverId: number, message?: string) => Promise<boolean>;
  acceptRequest: (requestId: number) => Promise<boolean>;
  rejectRequest: (requestId: number) => Promise<boolean>;
  cancelRequest: (requestId: number) => Promise<boolean>;
  removeFriend: (friendId: number) => Promise<boolean>;
  updateFriendship: (
    friendId: number,
    nickname?: string,
    isBestFriend?: boolean,
    allowSave?: boolean
  ) => Promise<boolean>;
  searchUsers: (query: string) => Promise<void>;
  clearSearch: () => void;
  clearError: () => void;
}

export const useFriendsStore = create<FriendsState>((set, get) => ({
  // Initial state
  friends: [],
  incomingRequests: [],
  outgoingRequests: [],
  searchResults: [],
  stats: null,
  isLoading: false,
  error: null,

  // Fetch friend list
  fetchFriends: async () => {
    set({ isLoading: true, error: null });

    const response = await api.get<Friendship[]>('/friends');

    if (response.success && response.data) {
      set({ friends: response.data, isLoading: false });
    } else {
      set({
        isLoading: false,
        error: response.error?.message || 'Failed to fetch friends',
      });
    }
  },

  // Fetch incoming friend requests
  fetchIncomingRequests: async () => {
    const response = await api.get<FriendRequest[]>('/friends/requests/incoming');

    if (response.success && response.data) {
      set({ incomingRequests: response.data });
    }
  },

  // Fetch outgoing friend requests
  fetchOutgoingRequests: async () => {
    const response = await api.get<FriendRequest[]>('/friends/requests/outgoing');

    if (response.success && response.data) {
      set({ outgoingRequests: response.data });
    }
  },

  // Fetch friend stats
  fetchStats: async () => {
    const response = await api.get<FriendStats>('/friends/stats');

    if (response.success && response.data) {
      set({ stats: response.data });
    }
  },

  // Send friend request
  sendFriendRequest: async (receiverId: number, message?: string): Promise<boolean> => {
    set({ isLoading: true, error: null });

    const response = await api.post<FriendRequest | Friendship>('/friends/requests', {
      receiver_id: receiverId,
      message,
    });

    if (response.success) {
      // Refresh data
      get().fetchOutgoingRequests();
      get().fetchStats();
      set({ isLoading: false });
      return true;
    }

    set({
      isLoading: false,
      error: response.error?.message || 'Failed to send friend request',
    });
    return false;
  },

  // Accept friend request
  acceptRequest: async (requestId: number): Promise<boolean> => {
    set({ isLoading: true, error: null });

    const response = await api.post<Friendship>(`/friends/requests/${requestId}/accept`);

    if (response.success && response.data) {
      // Add new friend to list and remove from incoming requests
      set((state) => ({
        friends: [...state.friends, response.data as Friendship],
        incomingRequests: state.incomingRequests.filter((r) => r.id !== requestId),
        isLoading: false,
      }));
      get().fetchStats();
      return true;
    }

    set({
      isLoading: false,
      error: response.error?.message || 'Failed to accept request',
    });
    return false;
  },

  // Reject friend request
  rejectRequest: async (requestId: number): Promise<boolean> => {
    set({ isLoading: true, error: null });

    const response = await api.post(`/friends/requests/${requestId}/reject`);

    if (response.success) {
      // Remove from incoming requests
      set((state) => ({
        incomingRequests: state.incomingRequests.filter((r) => r.id !== requestId),
        isLoading: false,
      }));
      get().fetchStats();
      return true;
    }

    set({
      isLoading: false,
      error: response.error?.message || 'Failed to reject request',
    });
    return false;
  },

  // Cancel friend request (sent by me)
  cancelRequest: async (requestId: number): Promise<boolean> => {
    set({ isLoading: true, error: null });

    const response = await api.delete(`/friends/requests/${requestId}`);

    if (response.success) {
      // Remove from outgoing requests
      set((state) => ({
        outgoingRequests: state.outgoingRequests.filter((r) => r.id !== requestId),
        isLoading: false,
      }));
      get().fetchStats();
      return true;
    }

    set({
      isLoading: false,
      error: response.error?.message || 'Failed to cancel request',
    });
    return false;
  },

  // Remove friend
  removeFriend: async (friendId: number): Promise<boolean> => {
    set({ isLoading: true, error: null });

    const response = await api.delete(`/friends/${friendId}`);

    if (response.success) {
      // Remove from friends list
      set((state) => ({
        friends: state.friends.filter((f) => f.friend.id !== friendId),
        isLoading: false,
      }));
      get().fetchStats();
      return true;
    }

    set({
      isLoading: false,
      error: response.error?.message || 'Failed to remove friend',
    });
    return false;
  },

  // Update friendship (nickname, best friend, save grant)
  updateFriendship: async (
    friendId: number,
    nickname?: string,
    isBestFriend?: boolean,
    allowSave?: boolean
  ): Promise<boolean> => {
    set({ isLoading: true, error: null });

    const body: Record<string, unknown> = {};
    if (nickname !== undefined) body.nickname = nickname;
    if (isBestFriend !== undefined) body.is_best_friend = isBestFriend;
    if (allowSave !== undefined) body.allow_save = allowSave;

    const response = await api.patch<Friendship>(`/friends/${friendId}`, body);

    if (response.success && response.data) {
      // Update friend in list
      set((state) => ({
        friends: state.friends.map((f) =>
          f.friend.id === friendId ? response.data as Friendship : f
        ),
        isLoading: false,
      }));
      return true;
    }

    set({
      isLoading: false,
      error: response.error?.message || 'Failed to update friendship',
    });
    return false;
  },

  // Search users
  searchUsers: async (query: string) => {
    if (query.length < 2) {
      set({ searchResults: [] });
      return;
    }

    set({ isLoading: true, error: null });

    const response = await api.get<UserSearchResult[]>('/friends/search', { params: { q: query } });

    if (response.success && response.data) {
      set({ searchResults: response.data, isLoading: false });
    } else {
      set({
        searchResults: [],
        isLoading: false,
        error: response.error?.message || 'Search failed',
      });
    }
  },

  // Clear search results
  clearSearch: () => set({ searchResults: [] }),

  // Clear error message
  clearError: () => set({ error: null }),
}));
